/**
 * CustomLayerSource
 *
 * Presentational MapLibre source + layer stack for a single custom layer's
 * GeoJSON (line / fill / outline / point), styled from the layer's serialized
 * {@link LayerStyle}. Source-agnostic: the GeoJSON is fetched by the caller, so
 * both the authenticated and the public custom-layer renderers reuse this.
 */
import { memo, useMemo } from 'react';
import { Source, Layer } from '@vis.gl/react-maplibre';
import type { FeatureCollection } from 'geojson';

import type { LayerStyle } from '@/types/layers';

const DEFAULT_LAYER_COLOR = '#3b82f6';
const POINT_STROKE_COLOR = '#ffffff';
const DEFAULT_OPACITY = 0.8;
const DEFAULT_LINE_WIDTH = 2;
const DEFAULT_FILL_OPACITY = 0.2;
const OUTLINE_WIDTH_RATIO = 0.75;
const POINT_RADIUS = 5;
const POINT_STROKE_WIDTH = 1;

function parseLayerStyle(styleJson: string | null): LayerStyle {
  const defaults: LayerStyle = {
    color: DEFAULT_LAYER_COLOR,
    opacity: DEFAULT_OPACITY,
    lineWidth: DEFAULT_LINE_WIDTH,
    fillOpacity: DEFAULT_FILL_OPACITY,
  };
  if (!styleJson) return defaults;
  try {
    return JSON.parse(styleJson) as LayerStyle;
  } catch {
    return defaults;
  }
}

interface CustomLayerSourceProps {
  layerId: string;
  geoJson: FeatureCollection;
  styleJson: string | null;
}

export const CustomLayerSource = memo(function CustomLayerSource({
  layerId,
  geoJson,
  styleJson,
}: CustomLayerSourceProps) {
  const style = useMemo(() => parseLayerStyle(styleJson), [styleJson]);
  const sourceId = `custom-layer-${layerId}`;
  const color = style.color ?? DEFAULT_LAYER_COLOR;
  const opacity = style.opacity ?? DEFAULT_OPACITY;
  const lineWidth = style.lineWidth ?? DEFAULT_LINE_WIDTH;

  return (
    <Source id={sourceId} type="geojson" data={geoJson}>
      {/* Line layer for LineString geometries */}
      <Layer
        id={`${sourceId}-line`}
        type="line"
        paint={{
          'line-color': color,
          'line-width': lineWidth,
          'line-opacity': opacity,
        }}
        filter={[
          'any',
          ['==', ['geometry-type'], 'LineString'],
          ['==', ['geometry-type'], 'MultiLineString'],
        ]}
      />

      {/* Fill layer for Polygon geometries */}
      <Layer
        id={`${sourceId}-fill`}
        type="fill"
        paint={{
          'fill-color': color,
          'fill-opacity': style.fillOpacity ?? DEFAULT_FILL_OPACITY,
        }}
        filter={[
          'any',
          ['==', ['geometry-type'], 'Polygon'],
          ['==', ['geometry-type'], 'MultiPolygon'],
        ]}
      />

      {/* Outline for Polygon geometries */}
      <Layer
        id={`${sourceId}-outline`}
        type="line"
        paint={{
          'line-color': color,
          'line-width': lineWidth * OUTLINE_WIDTH_RATIO,
          'line-opacity': opacity,
        }}
        filter={[
          'any',
          ['==', ['geometry-type'], 'Polygon'],
          ['==', ['geometry-type'], 'MultiPolygon'],
        ]}
      />

      {/* Circle layer for Point geometries */}
      <Layer
        id={`${sourceId}-point`}
        type="circle"
        paint={{
          'circle-color': color,
          'circle-radius': POINT_RADIUS,
          'circle-opacity': opacity,
          'circle-stroke-color': POINT_STROKE_COLOR,
          'circle-stroke-width': POINT_STROKE_WIDTH,
        }}
        filter={[
          'any',
          ['==', ['geometry-type'], 'Point'],
          ['==', ['geometry-type'], 'MultiPoint'],
        ]}
      />
    </Source>
  );
});
